import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, AlertCircle, CheckCircle, Clock, Eye, X, ExternalLink, Image,
  FileText, User, Mail, Phone, RefreshCw, Filter, ChevronDown, ChevronUp,
  MessageSquare, Check, XCircle, Search as SearchIcon
} from 'lucide-react';
import { dataCorrections } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

// ==================== CATEGORY LABELS ====================
const CATEGORY_LABELS = {
  school_info: 'School Information',
  exam_results: 'Exam Results & Rankings',
  admission_info: 'Admission Information',
  fees_tuition: 'Fees & Tuition',
  school_contacts: 'School Contacts',
  curriculum: 'Curriculum & Programs',
  extracurricular: 'Extracurricular Activities',
  education_providers: 'Education Providers Directory',
  other: 'Other'
};

// ==================== STATUS CONFIG ====================
const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'yellow', icon: Clock },
  reviewing: { label: 'Reviewing', color: 'blue', icon: Eye },
  verified: { label: 'Verified', color: 'purple', icon: CheckCircle },
  fixed: { label: 'Fixed', color: 'green', icon: Check },
  rejected: { label: 'Rejected', color: 'red', icon: XCircle }
};

// ==================== STATUS BADGE ====================
const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;

  const colors = {
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    blue: 'bg-blue-100 text-blue-800 border-blue-300',
    purple: 'bg-purple-100 text-purple-800 border-purple-300',
    green: 'bg-green-100 text-green-800 border-green-300',
    red: 'bg-red-100 text-red-800 border-red-300'
  };

  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${colors[config.color]}`}>
      <Icon className="w-4 h-4" />
      {config.label}
    </span>
  );
};

// ==================== ADMIN DATA CORRECTIONS PAGE ====================
export const AdminDataCorrectionsPage = ({ setPage }) => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadData();
  }, [filterStatus]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reportsRes, statsRes] = await Promise.all([
        dataCorrections.getAll(filterStatus || null),
        dataCorrections.getStats()
      ]);
      setReports(reportsRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (reportId, newStatus) => {
    setUpdating(true);
    try {
      const { error } = await dataCorrections.updateStatus(
        reportId,
        newStatus,
        adminNotes || null,
        user?.id
      );

      if (error) throw error;

      // Refresh data
      await loadData();
      setSelectedReport(null);
      setAdminNotes('');
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const filteredReports = reports.filter(report => {
    if (filterCategory && report.category !== filterCategory) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        report.current_content?.toLowerCase().includes(query) ||
        report.correct_content?.toLowerCase().includes(query) ||
        report.reporter_email?.toLowerCase().includes(query) ||
        report.reporter_name?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-5 h-5" />
          Back to Admin
        </button>
        <button onClick={loadData} className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-10 h-10" />
          <div>
            <h1 className="text-2xl font-bold">Data Correction Reports</h1>
            <p className="text-blue-100">Review and manage community-submitted corrections</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Object.entries(STATUS_CONFIG).map(([key, config]) => {
            const Icon = config.icon;
            const count = stats[key] || 0;
            return (
              <button
                key={key}
                onClick={() => setFilterStatus(filterStatus === key ? '' : key)}
                className={`p-4 rounded-xl border-2 transition-all ${
                  filterStatus === key
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <Icon className={`w-6 h-6 mb-2 ${filterStatus === key ? 'text-blue-600' : 'text-gray-400'}`} />
                <div className="text-2xl font-bold">{count}</div>
                <div className="text-sm text-gray-600">{config.label}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search reports..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Categories</option>
          {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {/* Reports List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : filteredReports.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center border">
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-800 mb-2">No Reports Found</h3>
          <p className="text-gray-500">
            {filterStatus || filterCategory
              ? 'Try adjusting your filters'
              : 'No data correction reports have been submitted yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredReports.map(report => (
            <div
              key={report.id}
              className="bg-white rounded-xl border shadow-sm hover:shadow-md transition-shadow overflow-hidden"
            >
              {/* Report Header */}
              <div
                className="p-4 cursor-pointer"
                onClick={() => setSelectedReport(selectedReport?.id === report.id ? null : report)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <StatusBadge status={report.status} />
                      <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {CATEGORY_LABELS[report.category] || report.category}
                      </span>
                      {report.section && (
                        <span className="text-sm text-gray-400">/ {report.section}</span>
                      )}
                    </div>
                    <p className="text-gray-800 font-medium line-clamp-2">{report.current_content}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {report.reporter_name || 'Anonymous'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {report.reporter_email}
                      </span>
                      <span>{new Date(report.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {report.proof_url && <ExternalLink className="w-5 h-5 text-blue-500" />}
                    {report.proof_image_url && <Image className="w-5 h-5 text-green-500" />}
                    {selectedReport?.id === report.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {selectedReport?.id === report.id && (
                <div className="border-t bg-gray-50 p-4 space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* What it says */}
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h5 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                        <XCircle className="w-4 h-4" /> What it currently says
                      </h5>
                      <p className="text-red-900 whitespace-pre-wrap">{report.current_content}</p>
                    </div>

                    {/* What it should be */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h5 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" /> What it should be
                      </h5>
                      <p className="text-green-900 whitespace-pre-wrap">{report.correct_content}</p>
                    </div>
                  </div>

                  {/* Proof */}
                  {(report.proof_url || report.proof_image_url) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h5 className="font-semibold text-blue-800 mb-2">Proof Provided</h5>
                      {report.proof_url && (
                        <a
                          href={report.proof_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-blue-600 hover:underline mb-2"
                        >
                          <ExternalLink className="w-4 h-4" />
                          {report.proof_url}
                        </a>
                      )}
                      {report.proof_image_url && (
                        <img
                          src={report.proof_image_url}
                          alt="Proof"
                          className="max-w-full max-h-64 rounded-lg border border-blue-200"
                        />
                      )}
                    </div>
                  )}

                  {/* Reporter Info */}
                  <div className="bg-white border rounded-lg p-4">
                    <h5 className="font-semibold text-gray-800 mb-2">Reporter Information</h5>
                    <div className="grid md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Name:</span>{' '}
                        <span className="font-medium">{report.reporter_name || 'Not provided'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Email:</span>{' '}
                        <span className="font-medium">{report.reporter_email}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Phone:</span>{' '}
                        <span className="font-medium">{report.reporter_phone || 'Not provided'}</span>
                      </div>
                    </div>
                    <div className="mt-2 text-sm">
                      <span className="text-gray-500">Notify when fixed:</span>{' '}
                      <span className={`font-medium ${report.notify_when_fixed ? 'text-green-600' : 'text-gray-400'}`}>
                        {report.notify_when_fixed ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>

                  {/* Admin Notes & Actions */}
                  <div className="bg-white border rounded-lg p-4">
                    <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" /> Admin Notes & Actions
                    </h5>

                    {report.admin_notes && (
                      <div className="bg-gray-100 rounded-lg p-3 mb-4 text-sm">
                        <p className="text-gray-700">{report.admin_notes}</p>
                        {report.reviewed_at && (
                          <p className="text-gray-500 text-xs mt-2">
                            Reviewed: {new Date(report.reviewed_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}

                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Add admin notes (optional)..."
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 mb-4"
                      rows={2}
                    />

                    <div className="flex flex-wrap gap-2">
                      {report.status !== 'reviewing' && (
                        <button
                          onClick={() => updateStatus(report.id, 'reviewing')}
                          disabled={updating}
                          className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 disabled:opacity-50 flex items-center gap-2"
                        >
                          <Eye className="w-4 h-4" /> Mark Reviewing
                        </button>
                      )}
                      {report.status !== 'verified' && (
                        <button
                          onClick={() => updateStatus(report.id, 'verified')}
                          disabled={updating}
                          className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 disabled:opacity-50 flex items-center gap-2"
                        >
                          <CheckCircle className="w-4 h-4" /> Verify
                        </button>
                      )}
                      {report.status !== 'fixed' && (
                        <button
                          onClick={() => updateStatus(report.id, 'fixed')}
                          disabled={updating}
                          className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 disabled:opacity-50 flex items-center gap-2"
                        >
                          <Check className="w-4 h-4" /> Mark Fixed
                        </button>
                      )}
                      {report.status !== 'rejected' && (
                        <button
                          onClick={() => updateStatus(report.id, 'rejected')}
                          disabled={updating}
                          className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50 flex items-center gap-2"
                        >
                          <XCircle className="w-4 h-4" /> Reject
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDataCorrectionsPage;
